import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Link, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { memo } from 'react';
import { ComponentLoader } from '../../../../common/components/ComponentLoader';
import { SeverityBadge } from '../../../../common/instances/components/SeverityBadge';
import { Issue } from '../models/Issue';
import { IssueDetailsSkeleton } from './skeletons/IssueDetailsSkeleton';

type Props = {
  issue: Issue | null;
  loading: boolean;
  initialLoad: boolean;
};

export const IssueDetails = memo(({ issue, loading, initialLoad }: Props) => {
  if (initialLoad || !issue) {
    return <IssueDetailsSkeleton />;
  }

  const title = issue.getTitle();
  const number = issue.getNumber();
  const severity = issue.getSeverity();
  const explanation = issue.getExplanation();
  const consequences = issue.getConsequences();
  const access = issue.getAccess();
  const expertise = issue.getExpertise();
  const resources = issue.getResources();
  const kbLinks = issue.getKbLinks();

  return (
    <ComponentLoader loading={loading}>
      <Accordion className="issue-details">
        <AccordionSummary
          id={`issue-details-${number}-header`}
          aria-controls={`issue-details-${number}-content`}
          expandIcon={<ExpandMore />}
          title={t('a11y.report.issues.issueDetailsAccordion', { issueNumber: number, issueName: title })}
          sx={{ minHeight: '4rem' }}
        >
          <Stack direction="row" alignItems="start" spacing={2}>
            <SeverityBadge severity={severity}>{issue.getNumber()}</SeverityBadge>
            <Typography variant="h6" component="p" lineHeight="1.2">
              {title}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2} sx={{ pb: 1 }}>
            {consequences && (
              <Box>
                <Typography fontWeight={500} gutterBottom>
                  {t('consequences')}
                </Typography>
                <Typography>{consequences}</Typography>
              </Box>
            )}
            {explanation && (
              <Box>
                <Typography fontWeight={500} gutterBottom>
                  {t('explanation')}
                </Typography>
                <Typography>{explanation}</Typography>
              </Box>
            )}
            {expertise && (
              <Box>
                <Typography fontWeight={500} gutterBottom>
                  {t('expertise')}
                </Typography>
                <Typography>{expertise}</Typography>
              </Box>
            )}
            {access && (
              <Box>
                <Typography fontWeight={500} gutterBottom>
                  {t('access')}
                </Typography>
                <Typography>{access}</Typography>
              </Box>
            )}
            {resources && (
              <Box>
                <Typography fontWeight={500} gutterBottom>
                  {t('resources')}
                </Typography>
                <Typography>{resources}</Typography>
              </Box>
            )}
            {kbLinks.length > 0 && (
              <Box>
                <Typography fontWeight={500} gutterBottom>
                  {t('references')}
                </Typography>
                <Stack spacing={1}>
                  {kbLinks.map((link, index) => {
                    const href = link.getHref();
                    const text = link.getText();
                    if (href) {
                      return (
                        <Link key={index} href={href} target="_blank">
                          {text}
                        </Link>
                      );
                    }
                    return (
                      <Typography key={index} component="span" sx={{ fontStyle: 'italic', lineHeight: '1.2' }}>
                        {text}
                      </Typography>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </ComponentLoader>
  );
});
